import React, { useState } from "react";
import { playResult, playFanfare } from "@/lib/feedbackSounds";
import { Check, X, Loader2, ListChecks } from "lucide-react";
import { api } from "@/lib/api";

const Question = ({ q, i, picked, onPick }) => (
  <div className="rounded-2xl border border-border p-3" data-testid={`helper-quiz-q-${i}`}>
    <p className="text-sm font-medium">{i + 1}. {q.q}</p>
    <div className="mt-2 grid gap-1.5">
      {q.options.map((o, j) => {
        const done = picked !== undefined;
        const tone = !done ? "border-border hover:border-primary" : j === q.answer ? "border-emerald-500 bg-emerald-500/10" : j === picked ? "border-destructive bg-destructive/10" : "border-border opacity-60";
        return (
          <button key={j} disabled={done} onClick={() => onPick(j)} data-testid={`helper-quiz-q-${i}-opt-${j}`}
            className={`flex items-center justify-between rounded-xl border px-3 py-1.5 text-left text-sm ${tone}`}>
            {o} {done && j === q.answer && <Check size={14} className="text-emerald-500" />}{done && j === picked && j !== q.answer && <X size={14} className="text-destructive" />}
          </button>
        );
      })}
    </div>
    {picked !== undefined && <p className="mt-2 text-xs text-muted-foreground" data-testid={`helper-quiz-q-${i}-why`}>{picked === q.answer ? "Correct! " : "Not quite. "}{q.why}</p>}
  </div>
);

// AI practice quiz: 5 fresh multiple-choice questions on the lesson.
export const HelperQuiz = ({ lessonId, body, onClose }) => {
  const [qs, setQs] = useState(null);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const req = React.useRef(0);
  const started = React.useRef(false);
  const load = async () => {
    const id = (req.current += 1);
    const avoid = (qs || []).map((q) => q.q);
    setBusy(true); setError(""); setPicks({}); setQs(null);
    try {
      const { data } = await api.post(`/helper/${lessonId}/quiz`, { ...body, avoid });
      if (id === req.current) setQs(data.questions);
    } catch (e) { if (id === req.current) setError(e.response?.data?.detail || "Couldn't make a quiz just now."); }
    finally { if (id === req.current) setBusy(false); }
  };
  React.useEffect(() => { if (!started.current) { started.current = true; load(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const answered = Object.keys(picks).length;
  const score = qs ? qs.filter((q, i) => picks[i] === q.answer).length : 0;
  const saved = React.useRef(null);
  React.useEffect(() => {
    if (qs && answered === qs.length && saved.current !== qs) {
      saved.current = qs;
      if (score === qs.length) playFanfare(0.5, "Music Helper quiz");
      api.post(`/helper/${lessonId}/quiz/result`, { score, total: qs.length, lesson_title: body.lesson_title }).catch(() => {});
    }
  }, [qs, answered, score, lessonId, body.lesson_title]);
  return (
    <div className="space-y-3" data-testid="helper-quiz">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-sm font-semibold"><ListChecks size={15} className="text-primary" /> Practice quiz</p>
        <button onClick={onClose} data-testid="helper-quiz-close" className="text-xs text-muted-foreground hover:text-primary">Back to chat</button>
      </div>
      {busy && <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="helper-quiz-loading"><Loader2 size={14} className="animate-spin" /> Writing 5 questions for you…</p>}
      {error && <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="helper-quiz-error">{error}</p>}
      {qs?.map((q, i) => <Question key={i} q={q} i={i} picked={picks[i]} onPick={(j) => { if (picks[i] !== undefined) return; playResult(j === q.answer); setPicks((p) => ({ ...p, [i]: j })); }} />)}
      {qs && answered === qs.length && (
        <div className="flex items-center justify-between rounded-2xl bg-primary/10 p-3" data-testid="helper-quiz-score">
          <p className="text-sm font-semibold">You scored {score} / {qs.length}</p>
          <button onClick={load} disabled={busy} data-testid="helper-quiz-again" className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">New questions</button>
        </div>
      )}
    </div>
  );
};
